<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { TNotification } from "@/api/models/message/notification";
import {
  getNotificationIconDisplay,
  isNotificationUnread,
} from "@/composables/models/notification/useNotification";
import { useNotificationTarget } from "@/composables/models/notification/useNotificationTarget";
import { getRelativeTimeFormat } from "@/utils/MessageHelpers";

const props = defineProps<{
  notification: TNotification;
}>();

const emit = defineEmits<{
  navigate: [];
}>();

const { t } = useI18n();
const { targetAction } = useNotificationTarget(() => props.notification);

const display = computed(() => getNotificationIconDisplay(props.notification.notification_type));

const isUnread = computed(() => isNotificationUnread(props.notification));

const targetRoute = computed(() => targetAction.value?.route ?? "/notificationsCenter");
</script>

<template>
  <router-link
    class="tw:flex tw:items-center tw:gap-4 tw:!px-5 tw:!py-3 tw:hover:bg-on-surface/3 tw:!no-underline tw:!text-inherit"
    :to="targetRoute"
    @click="emit('navigate')"
  >
    <div
      class="tw:w-11 tw:h-11 tw:rounded-xl tw:flex tw:items-center tw:justify-center tw:flex-shrink-0"
      :class="display.classes"
    >
      <v-icon
        :icon="display.icon"
        size="22"
      />
    </div>

    <div class="tw:flex-1 tw:min-w-0">
      <div class="tw:text-sm tw:font-bold tw:text-on-surface tw:truncate">
        {{ t(`models.notification.types.${notification.notification_type}`) }}
      </div>
      <div
        class="tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-on-surface/50 tw:!mt-0.5 tw:min-w-0"
      >
        <span
          v-if="notification.title"
          class="tw:bg-on-surface/5 tw:rounded tw:!px-1.5 tw:!py-px tw:text-on-surface/60 tw:font-medium tw:truncate"
        >
          {{ notification.title }}
        </span>
        <span class="tw:flex-shrink-0">
          · {{ getRelativeTimeFormat(new Date(notification.created_at), t) }}
        </span>
      </div>
    </div>

    <span
      class="tw:size-2 tw:rounded-full tw:flex-shrink-0"
      :class="isUnread ? 'tw:bg-secondary' : 'tw:bg-transparent'"
      :aria-label="isUnread ? t('models.notification.unread') : undefined"
      data-testid="notification-unread-dot"
    />
  </router-link>
</template>
